import useDevice from "@/hooks/useDevice";
import useLocalization from "@/hooks/useLocalization";
import useReservation from "@/hooks/useReservation";
import useVenue from "@/hooks/useVenue";
import icons from "@/tools/icons";
import { Button, Stack } from "@mui/material";
import { useMemo } from "react";
import SearchSelect from "./SearchSelect";

type SearchMenuProps = {
  onSearch?: () => Promise<void>;
};

export default function SearchMenu({ onSearch }: SearchMenuProps) {
  const {
    productId,
    setProductId,
    setVenueId,
    venueId,
    fetchInitialReservations,
    hasSearchParams,
    resetSearch,
  } = useReservation();

  const { venues, venueMap, productMap } = useVenue();
  const venue = useMemo(() => venueMap.get(venueId ?? ""), [venueId, venueMap]);
  const product = useMemo(
    () => productMap.get(productId ?? ""),
    [productId, productMap],
  );
  const allProducts = useMemo(
    () => Array.from(productMap.values()),
    [productMap],
  );
  const filteredProducts = useMemo(() => {
    if (venueId && venue) return venue.products ?? [];
    return allProducts;
  }, [allProducts, venue, venueId]);

  const filteredVenues = useMemo(() => {
    if (!venueId && productId && product) return product.venues ?? [];
    return venues;
  }, [product, productId, venueId, venues]);

  const { isMobile } = useDevice();
  const { t } = useLocalization();

  return (
    <Stack direction="column" gap={1}>
      <SearchSelect
        value={venueId}
        setValue={setVenueId}
        options={filteredVenues}
        label={t("nouns:venue")}
      />
      <SearchSelect
        value={productId}
        setValue={setProductId}
        options={filteredProducts}
        label={t("nouns:tour")}
      />
      <Stack direction="row" gap={1}>
        <Button
          fullWidth
          variant="contained"
          onClick={async () => {
            await onSearch?.();
            await fetchInitialReservations({
              venueId: venueId ?? undefined,
              productId: productId ?? undefined,
            });
          }}
        >
          {t("command:search")}
        </Button>
        {isMobile && (
          <Button
            variant="contained"
            color="inherit"
            disabled={!hasSearchParams}
            onClick={async () => {
              await onSearch?.();
              resetSearch();
            }}
          >
            <icons.Close />
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
